import { useMemo, useRef, useEffect, useState } from "react";
import { CalendarEvent, EVENT_TYPE_CONFIG } from "@/hooks/useCalendarEvents";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { EventHoverActions } from "./EventHoverActions";
import { dateKey, getWeekDates, layoutTimedEvents, minutesToLabel } from "./calendarLayoutUtils";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { Plus } from "lucide-react";

const HOUR_HEIGHT = 60; // pixels per hour
const HOURS = Array.from({ length: 24 }, (_, i) => i); // 0-23
const SCROLL_TO_HOUR = 7; // auto-scroll to 7am on mount

interface Props {
  selectedDate: Date;
  events: CalendarEvent[];
  layers: Record<string, boolean>;
  /** Pass a single date for day view, or leave undefined for week view */
  dates?: Date[];
  onDayClick?: (d: Date) => void;
  onEmptyCellClick?: (d: Date) => void;
  onEventPreview?: (ev: CalendarEvent) => void;
  onEventEdit?: (ev: CalendarEvent) => void;
  highlightEventId?: string | null;
}

export const CalendarWeekView = ({
  selectedDate, events, layers, dates,
  onDayClick, onEmptyCellClick, onEventPreview, onEventEdit, highlightEventId,
}: Props) => {
  const isMobile = useIsMobile();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentMinutes, setCurrentMinutes] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });

  const weekDays = useMemo(() => dates ?? getWeekDates(selectedDate), [dates, selectedDate]);
  const todayKey = dateKey(new Date());

  const filtered = useMemo(() => events.filter((e) => layers[e.event_type] !== false), [events, layers]);

  // Treat events without a start_time as all-day (defensive: legacy/imported events).
  const isAllDayLike = (e: CalendarEvent) => e.all_day || !e.start_time;

  // Group all-day (or timeless) events by date
  const allDayByDate = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    filtered.filter(isAllDayLike).forEach((e) => {
      const start = new Date(e.start_date + "T00:00:00");
      const end = e.end_date ? new Date(e.end_date + "T00:00:00") : start;
      const cur = new Date(start);
      while (cur <= end) {
        const k = dateKey(cur);
        if (!map[k]) map[k] = [];
        map[k].push(e);
        cur.setDate(cur.getDate() + 1);
      }
    });
    return map;
  }, [filtered]);

  // Group timed events by date and compute layout
  const layoutByDate = useMemo(() => {
    const timedMap: Record<string, CalendarEvent[]> = {};
    filtered.filter((e) => !isAllDayLike(e)).forEach((e) => {
      const k = e.start_date;
      if (!timedMap[k]) timedMap[k] = [];
      timedMap[k].push(e);
    });
    const result: Record<string, ReturnType<typeof layoutTimedEvents>> = {};
    for (const [k, evts] of Object.entries(timedMap)) {
      result[k] = layoutTimedEvents(evts, HOUR_HEIGHT);
    }
    return result;
  }, [filtered]);

  // Auto-scroll to SCROLL_TO_HOUR on mount
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = SCROLL_TO_HOUR * HOUR_HEIGHT;
    }
  }, []);

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setCurrentMinutes(now.getHours() * 60 + now.getMinutes());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const gutterWidth = isMobile ? 40 : 56;
  const totalGridHeight = 24 * HOUR_HEIGHT;

  const handleCellClick = (d: Date, e: React.MouseEvent<HTMLDivElement>) => {
    if (!onEmptyCellClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top + (scrollRef.current?.scrollTop ?? 0);
    // Don't open if they clicked on an event
    if ((e.target as HTMLElement).closest("[data-event]")) return;
    onEmptyCellClick(d);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Sticky header: day names + dates */}
      <div className="flex border-b bg-card z-10 shrink-0">
        <div style={{ width: gutterWidth }} className="border-r shrink-0" />
        {weekDays.map((d) => {
          const k = dateKey(d);
          const isToday = k === todayKey;
          return (
            <div key={k} className="flex-1 border-r py-1 text-center min-w-0 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => onDayClick?.(d)}>
              <div className="text-[10px] text-muted-foreground uppercase">
                {d.toLocaleDateString("en-US", { weekday: isMobile ? "narrow" : "short" })}
              </div>
              <div className={cn(
                "text-sm font-semibold mx-auto h-7 w-7 flex items-center justify-center rounded-full",
                isToday && "bg-primary text-primary-foreground"
              )}>
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day row */}
      <div className="flex border-b bg-muted/20 shrink-0">
        <div style={{ width: gutterWidth }} className="border-r text-[9px] text-muted-foreground p-1 flex items-center justify-end pr-2 shrink-0">
          all-day
        </div>
        {weekDays.map((d) => {
          const k = dateKey(d);
          const dayEvents = allDayByDate[k] || [];
          return (
            <div key={k} className="flex-1 border-r p-0.5 min-h-[28px] space-y-0.5">
              {dayEvents.slice(0, 3).map((ev) => {
                const cfg = EVENT_TYPE_CONFIG[ev.event_type];
                return (
                  <HoverCard key={ev.id} openDelay={300} closeDelay={150}>
                    <HoverCardTrigger asChild>
                      <button className={cn("w-full text-[9px] px-1 py-0.5 rounded truncate text-left", cfg.bgClass, cfg.textClass, highlightEventId === ev.id && "ring-2 ring-primary ring-offset-1 animate-pulse")}>
                        {ev.title}
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent className="w-auto p-0" align="start" side="bottom">
                      <EventHoverActions event={ev} onPreview={(e) => onEventPreview?.(e)} onEdit={(e) => onEventEdit?.(e)} />
                    </HoverCardContent>
                  </HoverCard>
                );
              })}
              {dayEvents.length > 3 && (
                <span className="text-[8px] text-muted-foreground pl-0.5">+{dayEvents.length - 3}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="flex" style={{ height: totalGridHeight }}>
          {/* Hour gutter */}
          <div style={{ width: gutterWidth }} className="border-r shrink-0 relative">
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="absolute text-[10px] text-muted-foreground text-right pr-2 -mt-2"
                style={{ top: hour * HOUR_HEIGHT, width: gutterWidth }}
              >
                {hour === 0 ? "" : hour < 12 ? `${hour} AM` : hour === 12 ? "12 PM" : `${hour - 12} PM`}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((d) => {
            const k = dateKey(d);
            const isToday = k === todayKey;
            const layoutEvents = layoutByDate[k] || [];

            return (
              <div
                key={k}
                className="flex-1 border-r relative"
                onClick={(e) => handleCellClick(d, e)}
              >
                {/* Hour grid lines */}
                {HOURS.map((hour) => (
                  <div
                    key={hour}
                    className="absolute w-full border-b border-muted/50"
                    style={{ top: hour * HOUR_HEIGHT, height: HOUR_HEIGHT }}
                  >
                    {/* Half-hour line */}
                    <div className="absolute w-full border-b border-muted/30" style={{ top: HOUR_HEIGHT / 2 }} />
                  </div>
                ))}

                {/* Current time indicator */}
                {isToday && (
                  <div
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: currentMinutes * (HOUR_HEIGHT / 60) }}
                  >
                    <div className="flex items-center">
                      <div className="h-2.5 w-2.5 rounded-full bg-red-500 -ml-1" />
                      <div className="flex-1 h-[2px] bg-red-500" />
                    </div>
                  </div>
                )}

                {/* Positioned events */}
                {layoutEvents.map((le) => {
                  const cfg = EVENT_TYPE_CONFIG[le.event.event_type];
                  const isShort = le.height < 30;
                  return (
                    <HoverCard key={le.event.id} openDelay={300} closeDelay={150}>
                      <HoverCardTrigger asChild>
                        <button
                          data-event="true"
                          className={cn(
                            "absolute z-10 rounded-md px-1.5 py-0.5 text-left overflow-hidden cursor-pointer transition-shadow hover:shadow-md border-l-3",
                            cfg.bgClass, cfg.textClass, cfg.borderClass,
                            highlightEventId === le.event.id && "ring-2 ring-primary ring-offset-1 animate-pulse"
                          )}
                          style={{
                            top: le.top,
                            height: le.height,
                            left: `calc(${le.leftPercent}% + 2px)`,
                            width: `calc(${le.widthPercent}% - 4px)`,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEventPreview?.(le.event);
                          }}
                        >
                          {isShort ? (
                            <span className="text-[10px] font-medium truncate block">
                              {le.event.start_time?.slice(0, 5)} {le.event.title}
                            </span>
                          ) : (
                            <>
                              <span className="text-[11px] font-semibold block truncate">{le.event.title}</span>
                              <span className="text-[9px] opacity-70 block">
                                {minutesToLabel(le.startMinutes)} – {minutesToLabel(le.endMinutes)}
                              </span>
                            </>
                          )}
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-auto p-0" align="start" side="right">
                        <EventHoverActions event={le.event} onPreview={(e) => onEventPreview?.(e)} onEdit={(e) => onEventEdit?.(e)} />
                      </HoverCardContent>
                    </HoverCard>
                  );
                })}

                {/* Empty cell hover indicator */}
                {onEmptyCellClick && layoutEvents.length === 0 && (
                  <div className="absolute inset-0 group cursor-pointer">
                    <Plus className="h-4 w-4 text-muted-foreground/30 opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 left-1/2 -translate-x-1/2" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
